import { GetStaticPathsResult, NextPage } from 'next'
import { withApollo } from 'components/withApollo'
import { useRouter } from 'next/router'
import getStaticApolloProps from 'lib/getStaticApolloProps'
import { useLotScheduleQuery } from 'components/pages/LotSchedulePage/types/lotScheduleQuery'
import PageLayout from 'components/pages/PageLayout'
import React from 'react'
import LoadingPage from 'components/pages/LoadingPage'
import NotFoundPage from 'components/pages/NotFoundPage'
import PageMeta from 'components/PageMeta'
import ErrorPage from 'components/pages/ErrorPage'
import pathFromUrl from 'utils/pathFromUrl'
import sortBy from 'lodash/sortBy'
import LotSchedulePageContent from 'components/pages/LotSchedulePage/LotSchedulePageContent'

type Params = {
  seoName: string
  notes?: string
  asPath: string
}

type Props = {
  refetch: () => void
}

function LotSchedulePage() {
  const router = useRouter()
  const { seoName } = router.query as Params

  const { data, loading, error } = useLotScheduleQuery({
    variables: {
      seoName: seoName.toLowerCase()
    },
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first'
  })

  const lot = data?.lot
  const lotGroup = data?.lotGroup

  if (loading && !lot && !lotGroup) return <LoadingPage />
  const notFoundError =
    error &&
    error.message.includes(
      'Cannot return null for non-nullable field Query.lot'
    )
  if (error && !notFoundError) return <ErrorPage message={error.message} />
  if (((!lot || !lot.active) && !lotGroup) || notFoundError) {
    return <NotFoundPage search={seoName} />
  }

  const firstLot =
    lotGroup &&
    lotGroup.lots &&
    sortBy(lotGroup.lots, (l) => l.lotGroupPosition)[0]
  const heroLinks = {
    twitter: (lot ? lot.twitter : firstLot?.twitter) ?? null,
    facebook: (lot ? lot.facebook : firstLot?.facebook) ?? null,
    instagram: (lot ? lot.instagram : firstLot?.instagram) ?? null,
    tiktok: (lot && lot.tiktok) ?? null,
    website: (lot ? lot.website : firstLot?.website) ?? null
  }

  const hideMarketing =
    router.asPath.includes('boeing') || router.asPath.includes('homedepot')
  return (
    <PageLayout
      hideNavigation={hideMarketing}
      transparentOnTop={hideMarketing}
      fullWidth
      highlightCartItems
      staticPage
    >
      <>
        {lot && !lotGroup && (
          <>
            <PageMeta
              canonicalPath={pathFromUrl(lot.lotUrl)}
              coordinates={[lot.latitude, lot.longitude]}
              description={`View food truck schedule for ${lot.fullAddress}`}
              image={lot.foodImage ? lot.foodImage.medium : lot.image?.medium}
              ldJson={data?.structuredData?.lotPage}
              title={`Best Food Truck | ${lot.name} schedule`}
              url={lot?.lotUrl}
            />
            <LotSchedulePageContent
              address={lot.fullAddress}
              heroLinks={heroLinks}
              isLotGroup={!!lotGroup}
              locationSchedule={lot.locationSchedule}
              name={lot.name}
              subscriptionLotId={lot.id}
            />
          </>
        )}

        {!lot && lotGroup && (
          <>
            <PageMeta
              canonicalPath={pathFromUrl(lotGroup.lotGroupUrl)}
              coordinates={[
                lotGroup?.lots?.[0]?.latitude ?? 0,
                lotGroup?.lots?.[0]?.longitude ?? 0
              ]}
              description={`View food truck schedule for ${lotGroup?.fullAddress}`}
              image={lotGroup.image?.medium}
              ldJson={data?.structuredData?.lotPage}
              title={`Best Food Truck | ${lotGroup.name} schedule`}
              url={lotGroup.lotGroupUrl}
            />
            <LotSchedulePageContent
              heroLinks={heroLinks}
              isLotGroup={!!lotGroup}
              locationSchedule={lotGroup.locationSchedule}
              name={lotGroup.name || lotGroup.address || ''}
              subscriptionLotId={null}
            />
          </>
        )}
      </>
    </PageLayout>
  )
}

export default withApollo({ ssr: false })(LotSchedulePage as NextPage)

export async function getStaticPaths(): Promise<GetStaticPathsResult> {
  return {
    paths: [],
    fallback: 'blocking'
  }
}

export const getStaticProps = getStaticApolloProps<Props, Params>(
  LotSchedulePage,
  {
    revalidate: 60 * 30 // 30 minutes
  }
)
